import React from "react";
import { CheckCheck } from "lucide-react";

interface WhatsappLivePreviewProps {
  contactName: string;
  statusLabel: string;
  /** When set (including empty string), renders the message bubble. */
  messageBody?: string | null;
  footer?: string | null;
  /** Optional header image shown above the message body (e.g. property photo). */
  headerImageUrl?: string | null;
  headerImageAlt?: string;
  emptyMessage?: string;
}

function contactInitial(name: string): string {
  const trimmed = name.trim();
  if (!trimmed) return "?";
  return trimmed.charAt(0).toUpperCase();
}

export const WhatsappLivePreview: React.FC<WhatsappLivePreviewProps> = ({
  contactName,
  statusLabel,
  messageBody,
  footer,
  headerImageUrl,
  headerImageAlt = "property",
  emptyMessage = "Select WhatsApp template to preview message",
}) => {
  const displayName = contactName.trim() || "Recipient";
  const timeLabel = new Date().toLocaleTimeString([], {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });

  return (
    <div className="w-[250px] max-w-full mx-auto h-[440px] min-h-[360px] flex flex-col rounded-[2rem] border-[4px] border-[#1a1a1a] overflow-hidden bg-[#1a1a1a] shadow-md">
      <div className="bg-[#1f4e47] text-white px-3 py-2.5 flex items-center gap-3">
        <div className="w-9 h-9 rounded-full bg-[#2d6a62] flex items-center justify-center shrink-0 text-sm font-medium">
          {contactInitial(displayName)}
        </div>
        <div className="min-w-0">
          <div className="text-[15px] font-semibold truncate leading-tight">
            {displayName}
          </div>
          <div className="text-[12px] text-white/75 truncate leading-tight">
            online · {statusLabel}
          </div>
        </div>
      </div>

      <div className="bg-[#E5DDD5] p-3 flex-1 min-h-0 overflow-y-auto">
        {messageBody != null ? (
          <div className="flex justify-end">
            <div className="max-w-[88%] bg-[#DCF8C6] rounded-lg rounded-br-none shadow-sm overflow-hidden">
              {headerImageUrl && (
                <img
                  src={headerImageUrl}
                  alt={headerImageAlt}
                  className="w-full max-h-[140px] object-cover"
                />
              )}
              <div className="px-2 py-1.5">
                <p className="text-[13.5px] leading-[18px] text-[#111b21] whitespace-pre-wrap">
                  {messageBody}
                  {footer && (
                    <>
                      {"\n"}
                      <span className="text-[12px] text-[#667781]">{footer}</span>
                    </>
                  )}
                  <span className="inline-block w-[62px] h-[3px]" aria-hidden />
                  <span className="float-right -mt-0.5 ml-1 inline-flex items-center gap-0.5 translate-y-[3px]">
                    <span className="text-[11px] leading-none text-[#667781]">
                      {timeLabel}
                    </span>
                    <CheckCheck
                      className="w-[15px] h-[15px] text-[#53bdeb] shrink-0"
                      strokeWidth={2.25}
                    />
                  </span>
                </p>
              </div>
            </div>
          </div>
        ) : (
          <div className="text-sm text-[#667781] italic text-center pt-12 px-4">
            {emptyMessage}
          </div>
        )}
      </div>
    </div>
  );
};
